import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import PaymentContainer from '@/components/payment-container/PaymentContainer.vue';
import {default as PaymentContainerClass} from '@/components/payment-container/PaymentContainerVue';
import { DtoCustomer } from '@/common/types/membership/DtoCustomer';
import { DtoDuesLineItem } from '@/common/types/membership/DtoLineItem';
import { DtoProduct } from '@/common/types/ecommerce/DtoProduct';
import { customerRepo } from '@/common/repositories/CustomerRepo';
import '@/common/filters/AllFiltersExport';
import { AxiosResponse } from 'axios';
import { MemberStatus } from '@/common/types/membership/MemberStatus';
import { ApiErrorResponse, isDuesCalculationFailedResponse, DtoMemberType, createPaymentProcessorOptions, PaymentProcessorOptions, PaymentContainerToken, PaymentTypeEnum, CustomerType, AutoPayOption } from '@/common/types/AllTypesExports';
import FieldValidationErrors from '../../components/errors/FieldValidationErrors.vue';
import EventBus from '@/common/services/EventBus';
import { ApiPaymentErrorResponse, createApiPaymentErrorResponse } from '@/common/types/Payment/ApiPaymentErrorResponse';
import { DtoProductQuantity, createProductQuantity } from '@/common/types/ecommerce/DtoProductQuantity';
import { ProductType } from '@/common/types/ecommerce/ProductType';
import regexPattern from '@/components/directives/regexPattern-directive';
import dayjs from 'dayjs';
import { AutoRenewType } from '@/common/types/membership/AutoRenewType';
import { DtoCustomerCreateResponse } from '../../common/types/membership/DtoCustomerCreateResponse';
import DonationInput from '../../components/inputs/donation/DonationInput.vue';
import QuantityInput from '../../components/inputs/quantity/QuantityInput.vue';
import { PaymentProcessingStatus } from '../../common/types/Payment/PaymentProcessingStatus';
import { IEventRegistrationGlobals } from '../event-registration/EventRegistrationGlobals';
import { CouponValidationResult, createCouponValidationResult } from '@/common/types/membership/CouponValidationResult';
import { DtoSeatTypeOption } from '@/common/types/membership/DtoSeatTypeOption';
import { DuesPreviewDiscount, getBillableDuesLineItems, getPerEligibleItemDuesDiscounts, getPerEligibleItemPercentageBases, limitDiscountsByAvailableUses, roundMoney, sumRoundedPercentageOfBases } from './billableDuesLineItems';
import SecretLinkStorage from '@/common/services/SecretLinkStorage';

declare let noviNs: IEventRegistrationGlobals;

@Component({
    components: {
        PaymentContainer,
        FieldValidationErrors,
        DonationInput,
        QuantityInput
    },
    directives: {
        regexPattern
    }
})
export default class MembershipCheckout extends Vue {
    @Prop({ required: true })
    customer!: DtoCustomer;
    @Prop({ required: true })
    memberType!: DtoMemberType;
    @Prop({ required: false, default: 0 })
    originalMemberTypeId!: number;
    @Prop({ required: false })
    addSeats!: boolean;
    @Prop({ required: false })
    isRenewal!: boolean;
    @Prop({ required: false })
    secretLink!: boolean;

    isDonatingTransactionFee: boolean = noviNs.paymentProcessorOptions.donateTransactionFeeChargeType?.toLowerCase() === 'OptOut'.toLowerCase() || noviNs.paymentProcessorOptions.donateTransactionFeeChargeType?.toLowerCase() === 'Mandatory'.toLowerCase();
    paymentType: string = PaymentTypeEnum.creditCard.toString();

    //Internal
    productTypes: object = ProductType;
    duesLineItems: DtoDuesLineItem[] = [];
    removedDuesLineItems: DtoDuesLineItem[] = [];
    addons: { product: DtoProduct, quantity: DtoProductQuantity }[] = [];
    membershipExpires?: Date | string;
    isLoading: boolean = false;
    errorMessage: string = "";
    duesCalculationFailed: boolean = false;
    duesCalculationErrorMessage: string = "";
    seatTypeOptions!: DtoSeatTypeOption[];

    // Promo codes
    couponResult: CouponValidationResult = createCouponValidationResult();
    validatedDiscountCode: string = "";
    discountCodeInput: string = "";
    applyingDiscount: boolean = false;
    discountError: string = "";
    isPromoInFocus: boolean = false;
    autoPayEnabled: boolean = this.customer != null ? this.customer.autoPay : false;

    paymentProcessorOptions: PaymentProcessorOptions = createPaymentProcessorOptions({
        initialTotal: 0,
        enableSaveCreditCard: true,
        enableAutoPay: this.memberType.autoRenewType != AutoRenewType.Disabled && this.memberType.autoRenewType != AutoRenewType.AdminInitiated,
        enableACH: noviNs.paymentProcessorOptions.enableACH,
        enablePaymentPlans: noviNs.paymentProcessorOptions.enablePaymentPlans,
        enableSaveAch: noviNs.paymentProcessorOptions.enableSaveAch,
        savedAchs: noviNs.paymentProcessorOptions.savedAchs,
        donateTransactionFeeLabel: noviNs.paymentProcessorOptions.donateTransactionFeeLabel,
        donateTransactionFeeDescription: noviNs.paymentProcessorOptions.donateTransactionFeeDescription,
        donateTransactionFeePercentage: noviNs.paymentProcessorOptions.donateTransactionFeePercentage,
        donateTransactionFeeChargeType: noviNs.paymentProcessorOptions.donateTransactionFeeChargeType,
        convenienceFeeTriggerAmount: noviNs.paymentProcessorOptions.convenienceFeeTriggerAmount,
        convenienceFeeIsEnabled: noviNs.paymentProcessorOptions.convenienceFeeIsEnabled,
        autoPayRequired: this.autoPayRequired,
        unauthorized: noviNs.paymentProcessorOptions.unauthorized,
        taxRate: noviNs.paymentProcessorOptions.taxRate,
        enableCreditCard: noviNs.paymentProcessorOptions.enableCreditCard
    });

    mounted() {
        var firstInput = this.$el.querySelector("input");
        if (firstInput) {
            (<HTMLElement>firstInput).focus();
        }

        EventBus.$on("file-uploaded", this.clearFileUploadErrors);
    }

    get disableSubmit() {
        return this.hasError || this.isLoading || (this.isPromoInFocus && this.discountCodeInput.trim());
    }

    get hasError(): boolean {
        return this.duesCalculationFailed || !!this.errorMessage;
    }

    get billableDuesLineItems() {
        return getBillableDuesLineItems(this.duesLineItems, item => item.line.price);
    }

    get duesTotal() {
        return this.billableDuesLineItems.reduce<number>((acc, line) => acc + line.line.price, 0);
    }

    get duesTotalEligibleForPaymentPlan() {
        return this.memberType.paymentPlansEnabled ? this.duesTotal : 0;
    }

    get addonTotal() {
        return this.addons.reduce<number>((acc, addon) => {
            if (this.isSoldOut(addon) || this.isOutsidePurchaseWindow(addon)) {
                addon.quantity.quantity = 0;
            }
            if (addon.product.productType == ProductType.Donation) {
                return acc + (addon.quantity.price ? addon.quantity.price : 0);
            }
            else {
                return acc + (addon.quantity.quantity * (addon.product.memberPrice ? addon.product.memberPrice : 0))
            }
        }, 0);
    }

    get addonTotalEligibleForPaymentPlan() {
        return this.addons.reduce<number>((acc, addon) => {
            if (this.isSoldOut(addon) || this.isOutsidePurchaseWindow(addon)) {
                addon.quantity.quantity = 0;
            }

            if (!addon.product.paymentPlansEnabled) {
                return acc;
            }

            if (addon.product.productType == ProductType.Donation) {
                return acc + (addon.quantity.price ? addon.quantity.price : 0);
            }
            else {
                return acc + (addon.quantity.quantity * (addon.product.memberPrice ? addon.product.memberPrice : 0))
            }
        }, 0);
    }

    get subTotal() {
        return Math.max(0, this.duesTotal + this.addonTotal);
    }

    get proposedDonateTransactionFeeAmount() {
        let ttl = this.subTotal - this.discount + this.taxTotal - this.proposedAmountForPaymentPlan;
        if (ttl <= 0) {
            return 0;
        }

        if (this.paymentProcessorOptions.convenienceFeeIsEnabled && (ttl >= this.paymentProcessorOptions.convenienceFeeTriggerAmount)) {
            return ttl * (this.paymentProcessorOptions.donateTransactionFeePercentage || 0);
        }

        return 0;
    }

    get donateTransactionFeeAmount() {
        if ((this.paymentType !== 'credit-card' && this.paymentType !== 'payment-plans') || !this.isDonatingTransactionFee) {
            return 0;
        }

        return this.proposedDonateTransactionFeeAmount;
    }

    get taxTotal() {
        if (this.paymentProcessorOptions.taxRate == undefined) {
            return 0;
        }

        var taxableDiscountTotal = this.getDiscountTotals()[3];

        let total = 0;
        this.addons.map((addon) => {
            if (addon.product.taxable) {
                total += addon.quantity.price * addon.quantity.quantity;
            }
        })

        this.billableDuesLineItems.map((lineItem) => {
            if (lineItem.line.taxable) {
                total += lineItem.line.price; // multiple years?
            }
        })

        if (taxableDiscountTotal > total) {
            return 0;
        }

        return (total - taxableDiscountTotal) * this.paymentProcessorOptions.taxRate;
    }

    get taxTotalForPaymentPlan() {
        if (this.paymentProcessorOptions.taxRate == undefined) {
            return 0;
        }

        // Payment-plan tax base is taxable addons only — do not subtract dues promo from it.
        var taxableAddonDiscountTotal = this.getDiscountTotals()[4];

        let total = 0;
        this.addons.map((addon) => {
            if (addon.product.taxable && addon.product.paymentPlansEnabled) {
                total += addon.quantity.price * addon.quantity.quantity;
            }
        })

        if (taxableAddonDiscountTotal > total) {
            return 0;
        }

        return (total - taxableAddonDiscountTotal) * this.paymentProcessorOptions.taxRate;
    }

    get total() {
        return Math.max(0, this.subTotal - this.discount + this.donateTransactionFeeAmount + this.taxTotal);
    }

    get autoPayRequired() {
        return this.memberType && this.memberType.autoRenewType == AutoRenewType.AutoPayRequired;
    }

    get eligibleForPaymentPlanAmount() {
        return this.duesTotalEligibleForPaymentPlan + this.addonTotalEligibleForPaymentPlan + this.taxTotalForPaymentPlan - this.discount;
    }

    get eligibleItemsForPaymentPlan() {
        var eligibleItemsForPaymentPlan: { ID: number; Type: string; Quantity: number; Renewal: boolean }[] = [];

        if (this.memberType.paymentPlansEnabled) {
            eligibleItemsForPaymentPlan.push({ ID: this.memberType.id, Type: "MemberType", Quantity: 1, Renewal: this.isRenewal });
        }

        this.addons.forEach(addon => {
            if (addon.quantity.quantity == 0 || addon.quantity.price == 0) {
                return;
            }

            if (addon.product.paymentPlansEnabled) {
                eligibleItemsForPaymentPlan.push({ ID: addon.product.id, Type: "Product", Quantity: addon.quantity.quantity, Renewal: false });
            }
        });

        return eligibleItemsForPaymentPlan;
    }

    get proposedAmountForPaymentPlan() {
        if (this.paymentType !== 'payment-plans') {
            return 0;
        }

        return this.eligibleForPaymentPlanAmount;
    }

    @Watch('total', { immediate: true })
    onTotalChanged(newTotal: number) {
        let paymentWidget = <PaymentContainerClass>this.$refs.payment;
        if (paymentWidget) {
            paymentWidget.onTotalUpdated(newTotal);
        }
    }

    @Watch('proposedDonateTransactionFeeAmount', { immediate: true })
    onProposedDonateTransactionFeeAmountUpdated(value: number) {
        const paymentContainer = this.$refs.payment as PaymentContainerClass;
        if (paymentContainer && paymentContainer.onProposedDonateTransactionFeeAmountUpdated) {
            paymentContainer.onProposedDonateTransactionFeeAmountUpdated(value);
        }
    }

    get subscriptionsPurchased() {
        return this.addons && this.addons.some((addOn) => addOn.product.isSubscription && addOn.quantity.quantity > 0);
    }

    @Watch('subscriptionsPurchased')
    onSubscriptionsPurchased(subscriptionsPresent: boolean) {
        let paymentContainer = <PaymentContainerClass>this.$refs.payment;
        paymentContainer.onForceSaveCardUpdated(subscriptionsPresent);
        paymentContainer.onForceSaveAchUpdated(subscriptionsPresent);
    }


    onAutoPayOptionsUpdated(autoPayOptions: AutoPayOption[]) {

        if (this.memberType.hasAutoPayRelatedDuesRules) {
            if (this.customer != null) {
                var currentCustomerOption = autoPayOptions.find(option => option.customerId == this.customer.id);
                if (currentCustomerOption != null) {
                    this.autoPayEnabled = currentCustomerOption.enabled;
                }
            }
        }
    }

    @Watch('autoPayEnabled')
    onAutoPayEnabledUpdated(newValue: boolean) {
        this.loadLineItemsWithRetry();
    }

    get formattedMembershipExpires(): string {
        if (!this.membershipExpires)
            return "";
        return new Date(this.membershipExpires).toLocaleDateString();
    }

    created() {
        if (this.memberType.seatBased || this.addSeats) {
            this.loadSeatTypeOptionsWithRetry()?.then(() => {
                this.loadLineItemsWithRetry();
                EventBus.$on("member-saved", this.loadLineItemsWithRetry);
            });
        } else {
            this.loadLineItemsWithRetry();
            EventBus.$on("member-saved", this.loadLineItemsWithRetry);
        }

        if (this.memberType.requireImmediatePayment) {
            this.paymentProcessorOptions.enableInvoice = true; // Always show the tab, but display proper warning message
            this.paymentProcessorOptions.invoiceWarningMessage = "Sorry, we don't offer invoicing for this membership type. Please purchase separately or pay with Credit/Debit or ACH.";
        }
        this.paymentProcessorOptions.customerForBillableParties = this.customer.id;
        this.paymentProcessorOptions.billablePartyId = this.customer.defaultBillablePartyId ?? this.customer.id;
        this.paymentProcessorOptions.submitButtonText = "Complete Checkout";
    }

    loadLineItemsWithRetry(maxRetries = 2, currentRetry = 0) {
        this.isLoading = true;
        this.errorMessage = "";
        this.duesCalculationFailed = false;
        this.duesCalculationErrorMessage = "";

        const applyLoadError = (axiosResponse?: AxiosResponse<ApiErrorResponse>) => {
            const data = axiosResponse?.data;
            if (isDuesCalculationFailedResponse(data)) {
                this.duesCalculationFailed = true;
                this.duesCalculationErrorMessage = data?.message || "";
                this.errorMessage = "";
            } else {
                this.duesCalculationFailed = false;
                this.duesCalculationErrorMessage = "";
                const apiMessage = data?.message?.trim() || "";
                this.errorMessage = apiMessage
                    ? `Unable to load the dues checkout information. ${apiMessage}`
                    : "Unable to load the dues checkout information. Please try again.";
            }
        };

        const loadDuesLines = () => {
            return customerRepo.getDuesLinesForCustomer(this.customer.id, this.memberType.id, this.addSeats, this.seatTypeOptions, this.autoPayEnabled)
                .then(result => {
                    this.errorMessage = "";
                    this.duesCalculationFailed = false;
                    this.duesCalculationErrorMessage = "";
                    this.duesLineItems = result.lineItems;
                    this.addons = result.addons.map((product) => {
                        var pq = createProductQuantity();
                        pq.id = product.id;
                        pq.quantity = !product.hasLimitedQuantity && !product.hasPurchaseWindow
                            ? (product.chargeByDefault || product.productType == ProductType.Donation ? 1 : 0)
                            : (product.maxAvailable > 0 && this.isProductWithinPurchaseWindow(product)
                                ? (product.chargeByDefault || product.productType == ProductType.Donation ? 1 : 0)
                                : 0);
                        pq.price = product.memberPrice ? product.memberPrice : 0;
                        return { product: product, quantity: pq };
                    });
                    this.membershipExpires = result.membershipExpires;
                })
                .catch((axiosResponse: AxiosResponse<ApiErrorResponse>) => {
                    throw axiosResponse;
                });
        };

        const retryLoad = (): Promise<void> => {
            return loadDuesLines().catch(async (axiosResponse: AxiosResponse<ApiErrorResponse>) => {
                const data = axiosResponse?.data;
                const shouldRetry = !isDuesCalculationFailedResponse(data) && currentRetry < maxRetries;
                if (shouldRetry) {
                    currentRetry++;
                    await new Promise(r => setTimeout(r, 250 * currentRetry));
                    return retryLoad();
                }

                applyLoadError(axiosResponse);
                throw axiosResponse;
            });
        };

        return retryLoad().finally(() => {
            this.isLoading = false;
        });
    }

    loadSeatTypeOptionsWithRetry(maxRetries = 2, currentRetry = 0) {

        if (!this.memberType.seatBased) {
            return;
        }

        const loadSeatTypeOptions = () => {
            return customerRepo.getSeatTypeOptions(this.customer.id, this.addSeats)
                .then(result => {
                    this.errorMessage = "";
                    this.seatTypeOptions = result;
                })
                .catch((axiosResponse: AxiosResponse<ApiErrorResponse>) => {
                    const detail = axiosResponse?.data?.message?.trim();
                    this.errorMessage = detail
                        ? `Unable to load seat type options for checkout. ${detail}`
                        : 'Unable to load seat type options for checkout. Please try again or contact us.';
                    throw axiosResponse;
                });
        }

        const retryLoad = (): Promise<void> => {
            return loadSeatTypeOptions().catch(error => {
                if (currentRetry < maxRetries) {
                    currentRetry++;
                    return retryLoad();
                } else {
                    throw error;
                }
            });
        };

        return retryLoad().finally(() => {
            this.isLoading = false;
        });
    }

    removeLine(lineIndex: number) {
        if (confirm("Are you sure that you want to remove this optional line item?")) {
            var removedItems = this.duesLineItems.splice(lineIndex, 1);
            if (removedItems.length > 0)
                this.removedDuesLineItems.push(removedItems[0]);
        }
    }

    clearCoupon() {
        this.couponResult = createCouponValidationResult();
        this.applyingDiscount = false;
        this.validatedDiscountCode = "";
        this.discountError = "";
    }   

    get discountDuesRules() {
        return this.billableDuesLineItems
            .filter(duesLine => this.couponResult.discountDuesRules.includes(duesLine.duesRuleId));
    }

    get discountableProducts() {
        return this.addons
            .filter(addon => addon.product.productType != ProductType.Donation && this.couponResult.discountProducts.includes(addon.product.id));
    }

    get discount() {
        var total = this.getDiscountTotals();

        return total[0];
    }

    getDiscountTotals(): [totalDiscount: number, duesTotalDiscount: number, addonTotalDiscount: number, taxableDiscount: number, taxableAddonDiscount: number] {
        let subTotal = 0;
        let duesTotal = 0;
        let addonTotal = 0;
        let taxableTotal = 0;
        let taxableAddonTotal = 0;

        // Percentage discount
        if (this.couponResult.discountType == 0) {
            // Build net amounts (positive + negative) and also a list of positive items for "Per Line Item" behaviour.
            let duesNet = 0;
            let addonNet = 0;
            let taxableDuesNet = 0;
            let taxableAddonNet = 0;

            this.discountDuesRules.forEach(duesRule => {
                const price = duesRule.line.price ?? 0;
                const taxable = !!duesRule.line.taxable;
                duesNet += price;
                if (taxable) {
                    taxableDuesNet += price;
                }
            });

            // Per Eligible Item expands seat units (same breakdown AmountOff uses).
            // Per Checkout ignores this list and uses duesNet above.
            let discounts: DuesPreviewDiscount[] = getPerEligibleItemPercentageBases(this.discountDuesRules);

            this.discountableProducts.forEach(addon => {
                const unitPrice = addon.product.memberPrice ?? 0;
                const qty = addon.quantity.quantity;
                const lineTotal = unitPrice * qty;
                const taxable = !!addon.product.taxable;

                addonNet += lineTotal;
                if (taxable) {
                    taxableAddonNet += lineTotal;
                }

                if (unitPrice <= 0 || qty <= 0) {
                    return;
                }

                // For per-line logic each unit gets its own entry
                for (let i = 0; i < qty; i++) {
                    discounts.push([unitPrice, "addon", taxable]);
                }
            });

            const netSubTotal = duesNet + addonNet;

            if (this.couponResult.discountMethod === 0) {
                // Per Checkout: discount is based on NET amount (positive + negative). If net is <= 0 or percent is 0, there is nothing to discount.
                if (netSubTotal <= 0 || this.couponResult.discountPercent <= 0) {
                    return [0, 0, 0, 0, 0];
                }

                const totalDiscount = roundMoney(netSubTotal * this.couponResult.discountPercent);
                const duesTotalDiscount = roundMoney(duesNet * this.couponResult.discountPercent);
                const addonTotalDiscount = roundMoney(addonNet * this.couponResult.discountPercent);
                const taxableDiscount = roundMoney((taxableDuesNet + taxableAddonNet) * this.couponResult.discountPercent);
                const taxableAddonDiscount = roundMoney(taxableAddonNet * this.couponResult.discountPercent);

                return [totalDiscount, duesTotalDiscount, addonTotalDiscount, taxableDiscount, taxableAddonDiscount];
            }

            // Per Eligible Item: work only with positive line items
            if (this.couponResult.availableUses != null && this.couponResult.availableUses < discounts.length) {
                discounts = limitDiscountsByAvailableUses(discounts, this.couponResult.availableUses);
            }

            const percent = this.couponResult.discountPercent;
            const duesDiscounts = discounts.filter(x => x[1] == "dues");
            const addonDiscounts = discounts.filter(x => x[1] == "addon");

            // Dues: round each seat × percent (matches PromoCodeService PEI dues).
            // Addons: round once on the summed bases (matches product group SubTotal × %).
            const duesTotalDiscount = sumRoundedPercentageOfBases(duesDiscounts, percent);
            const addonTotalDiscount = roundMoney(addonDiscounts.reduce((acc, d) => acc + d[0], 0) * percent);
            const taxableDiscount =
                sumRoundedPercentageOfBases(duesDiscounts.filter(x => x[2]), percent)
                + roundMoney(addonDiscounts.filter(x => x[2]).reduce((acc, d) => acc + d[0], 0) * percent);
            const taxableAddonDiscount = roundMoney(addonDiscounts.filter(x => x[2]).reduce((acc, d) => acc + d[0], 0) * percent);

            return [
                duesTotalDiscount + addonTotalDiscount,
                duesTotalDiscount,
                addonTotalDiscount,
                taxableDiscount,
                taxableAddonDiscount
            ];
        }

        // Amount off - Per Checkout
        if (this.couponResult.discountType == 1 && this.couponResult.discountMethod == 0) {
            this.discountDuesRules.forEach(duesRule => {
                const price = duesRule.line.price ?? 0;
                duesTotal += price;
                if (duesRule.line.taxable) {
                    taxableTotal += price;
                }
            });

            this.discountableProducts.forEach(addon => {
                const unitPrice = addon.product.memberPrice ?? 0;
                const qty = addon.quantity.quantity;
                const lineTotal = unitPrice * qty;
                addonTotal += lineTotal;
                if (addon.product.taxable) {
                    taxableAddonTotal += lineTotal;
                    taxableTotal += lineTotal;
                }
            });

            subTotal = duesTotal + addonTotal;

            if (subTotal <= 0 || this.couponResult.discountAmount <= 0) {
                return [0, 0, 0, 0, 0];
            }

            const totalDiscount = Math.min(subTotal, this.couponResult.discountAmount);
            const discountPercentageTotal = totalDiscount / subTotal;

            const duesTotalDiscount = duesTotal * discountPercentageTotal;
            const addonTotalDiscount = addonTotal * discountPercentageTotal;
            const taxableDiscount = taxableTotal * discountPercentageTotal;
            const taxableAddonDiscount = taxableAddonTotal * discountPercentageTotal;

            return [totalDiscount, duesTotalDiscount, addonTotalDiscount, taxableDiscount, taxableAddonDiscount];
        }

        // Amount off - Per Line Item
        if (this.couponResult.discountType == 1 && this.couponResult.discountMethod == 1) {
            let discounts = getPerEligibleItemDuesDiscounts(this.discountDuesRules, this.couponResult.discountAmount);

            this.discountableProducts.forEach(addon => {
                const unitPrice = addon.product.memberPrice ?? 0;
                const qty = addon.quantity.quantity;

                if (unitPrice <= 0 || qty <= 0) {
                    return;
                }

                const perLineDiscount = Math.min(unitPrice, this.couponResult.discountAmount);

                for (let i = 0; i < qty; i++) {
                    discounts.push([perLineDiscount, "addon", !!addon.product.taxable]);
                }
            });

            if (this.couponResult.availableUses != null && this.couponResult.availableUses < discounts.length) {
                discounts = limitDiscountsByAvailableUses(discounts, this.couponResult.availableUses);
            }

            subTotal = discounts.reduce((acc, price) => acc + price[0], 0);
            duesTotal = discounts.filter(x => x[1] == "dues").reduce((acc, price) => acc + price[0], 0);
            addonTotal = discounts.filter(x => x[1] == "addon").reduce((acc, price) => acc + price[0], 0);
            taxableTotal = discounts.filter(x => x[2]).reduce((acc, price) => acc + price[0], 0);
            taxableAddonTotal = discounts.filter(x => x[1] == "addon" && x[2]).reduce((acc, price) => acc + price[0], 0);

            return [subTotal, duesTotal, addonTotal, taxableTotal, taxableAddonTotal];
        }

        return [0, 0, 0, 0, 0];
    }


    get canShowStepper() {
        if (!this.memberType.seatBased) {
            return false;
        }

        // New signups always show seat controls when the type is seat-based.
        if (!this.isRenewal) {
            return true;
        }

        // Renewals show seats when expired, or when the member switched to a different seat-based type.
        const switchedMemberType = this.originalMemberTypeId > 0 && this.memberType.id !== this.originalMemberTypeId;

        return this.customer.membershipStatus === MemberStatus.Expired || switchedMemberType;
    }

    focusPromoInput() {
        this.isPromoInFocus = true;
    }

    applyCoupon() {
        this.clearCoupon();

        // Trim the input
        this.discountCodeInput = this.discountCodeInput.trim();

        if (!this.discountCodeInput) {
            this.isPromoInFocus = false;
            return;
        }
            

        this.applyingDiscount = true;


        customerRepo.validateCoupon(this.customer.id, this.memberType.id, this.addons.map(x => x.product.id), this.discountCodeInput)
            .then(result => {
                if (result.error) {
                    this.discountError = result.error;
                }
                else {
                    this.couponResult = result;
                    this.validatedDiscountCode = this.discountCodeInput;
                    this.discountCodeInput = "";
                }
            })
            .catch((axiosResponse: AxiosResponse<ApiErrorResponse>) => {
                this.discountError = `Unable to verify promo code: ${axiosResponse.data.message}`;
                throw axiosResponse;
            })
            .finally(() => {
                this.applyingDiscount = false; 
                this.isPromoInFocus = false;
            });
    }

    handleIsDonatingTransactionFeeUpdated(value: boolean) {
        this.isDonatingTransactionFee = value;
    }

    onPaymentTypeUpdated(paymentType: string) {
        this.paymentType = paymentType;

        if (this.autoPayEnabled && !this.customer.autoPay && (this.paymentType == PaymentTypeEnum.invoice || this.paymentType == PaymentTypeEnum.free)) {
            this.autoPayEnabled = false;
        }
    }

    onPaymentTokenReady(paymentToken: PaymentContainerToken) {
        var removedDuesRuleIds = this.removedDuesLineItems.map(l => l.duesRuleId);
        var addons = this.addons.map(pq => pq.quantity);

        var isAddSeats = this.addSeats && !this.isRenewal;

        let recaptchaToken: string | null | undefined;
        if (noviNs.reCaptchaUpgradeEnabled) {
            const input = document.querySelector<HTMLInputElement>('input[name="g-recaptcha-response"]');
            recaptchaToken = input?.value ?? null;
        }

        customerRepo.membershipCheckout(this.customer.id, paymentToken, removedDuesRuleIds, addons, isAddSeats, this.isDonatingTransactionFee, this.memberType.id, this.validatedDiscountCode, this.seatTypeOptions, this.secretLink, recaptchaToken)
            .then((result: DtoCustomerCreateResponse) => {
                if (result.lostUserSession) {
                    window.location.href = "/login?ReturnUrl=/member-compass/new-membership&ErrorMessage=Sorry, something went wrong with your checkout. Your account was created but you have not been charged yet. Please log in to complete your checkout.";
                    return;
                }

                SecretLinkStorage.clear(this.memberType.id);
                var memberCheckoutType = this.addSeats ? "add-seats" : this.customer.membershipExpires ? "renewal" : "new";
                if (result.isAutoLoggedIn) {
                    var memberTypeId = this.memberType.id;
                    window.location.href = `/member-compass?checkout-success=true&checkout-type=${memberCheckoutType}&member-type-id=${memberTypeId}`;
                } else {
                    window.location.href = `/confirm-email?userId=${result.userId}&checkout-type=${memberCheckoutType}`;
                }
            })
            .catch((axiosResponse: AxiosResponse<ApiPaymentErrorResponse>) => {
                let paymentWidget = <PaymentContainerClass>this.$refs.payment;
                if (axiosResponse.status >= 500) {
                    if (!axiosResponse.data || typeof axiosResponse.data !== 'object') {
                        axiosResponse.data = createApiPaymentErrorResponse(axiosResponse.data);
                        axiosResponse.data.message = `It appears your browser had an issue. In order to continue, please restart your browser and check your email or <a href="${noviNs.contactUsUrl}" target="_blank"><strong>contact us</strong></a> to see if your checkout went through.`;
                    }
                    axiosResponse.data.paymentProcessingStatus = PaymentProcessingStatus.Error;
                }
                paymentWidget.handleApiPaymentError(axiosResponse.data);
            });
    }

    isOutsidePurchaseWindow(addon: { product: DtoProduct, quantity: DtoProductQuantity }) {
        if (!addon.product.hasPurchaseWindow) {
            return false;
        }
        var isInPurchaseWindow = this.isProductWithinPurchaseWindow(addon.product);
        return !isInPurchaseWindow;
    }

    isSoldOut(addon: { product: DtoProduct, quantity: DtoProductQuantity }) {
        if (!addon.product.hasLimitedQuantity) {
            return false;
        }
        return addon.product.maxAvailable < 1;
    }

    @Watch("seatTypeOptions", { immediate: false, deep: true })
    updateLineItems() {
        this.loadLineItemsWithRetry();
    }

    private isProductWithinPurchaseWindow(product: DtoProduct) {
        if (!product.hasPurchaseWindow) {
            return true;
        }
        const now = dayjs();

        var isInPurchaseWindow = (now.isSame(product.purchaseWindowStart) || now.isAfter(product.purchaseWindowStart)) && (now.isSame(product.purchaseWindowEnd) || now.isBefore(product.purchaseWindowEnd));
        return isInPurchaseWindow;
    }

    private isEmpty(array: number[]) {
        return !array || array.length === 0;
    }

    get showPromoCode() {
        return this.memberType == null || this.memberType.hasPromoCodes;
    }

    clearFileUploadErrors() {
        this.errors.clear(CustomerType.Person);
    }
}
